import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AppState } from "@strands/agent-transport";

const MARKDOWN_PLUGINS = [remarkGfm];

/**
 * Canvas main view — a collaborative markdown scratchpad. The agent writes
 * via the `canvas_write` tool → STATE_DELTA → state.canvas.content; the
 * user reads and can toggle to edit mode. Saving a local edit calls
 * `updateState('canvas', ...)` which mutates the frontend state dict;
 * the new value travels to the backend on the next `sendMessage` inside
 * `RunAgentInput.state` (AG-UI idiom). The agent sees the user's version
 * on its next turn via the `canvas_read` tool.
 *
 * This means: client is the source of truth for `canvas` between runs,
 * backend holds the authoritative copy during a run, and the merge point
 * is the start of the next run. No separate REST endpoint — everything
 * flows through the AG-UI protocol's two channels (SSE down, run body up).
 *
 * Edit-mode contention rule: while the user is editing, incoming
 * STATE_DELTA updates to `canvas` are ignored — otherwise an agent that
 * writes mid-edit would nuke the user's in-progress text. When the user
 * exits edit mode (save or cancel), the view resyncs from state.
 *
 * See UI_TAXONOMY.md §4 (main slot, type E).
 */

interface CanvasState {
  content?: string;
  updated_at?: number;
}

interface Props {
  state: AppState;
  sessionId: string | null;
  updateState: (key: string, value: unknown) => void;
  onClose: () => void;
}

export function CanvasView({ state, updateState }: Props) {
  const canvas = (state.canvas as CanvasState | undefined) ?? {};
  const remoteContent = canvas.content ?? '';

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(remoteContent);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Resync draft from state when NOT editing — keeps the view live as the
  // agent writes. While editing, we intentionally ignore incoming updates
  // to avoid nuking in-progress text.
  useEffect(() => {
    if (!editing) setDraft(remoteContent);
  }, [remoteContent, editing]);

  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  const enterEdit = () => setEditing(true);

  const cancelEdit = () => {
    setDraft(remoteContent);
    setEditing(false);
  };

  const saveEdit = () => {
    // Write to local client state only — no network call. The new value
    // ships to the server on the next sendMessage via RunAgentInput.state,
    // and the agent can read it via `canvas_read` on its next turn.
    updateState('canvas', { content: draft, updated_at: Date.now() / 1000 });
    setEditing(false);
  };

  const empty = !remoteContent && !editing;

  return (
    <div className="canvas-view">
      <div className="canvas-toolbar">
        {editing ? (
          <>
            <button
              type="button"
              className="canvas-btn canvas-btn--primary"
              onClick={saveEdit}
              disabled={draft === remoteContent}
            >
              Сохранить
            </button>
            <button
              type="button"
              className="canvas-btn"
              onClick={cancelEdit}
            >
              Отмена
            </button>
            <span className="canvas-hint">
              Агент увидит ваши правки в следующем ответе
            </span>
          </>
        ) : (
          <>
            <button
              type="button"
              className="canvas-btn"
              onClick={enterEdit}
              disabled={empty && !remoteContent}
            >
              Редактировать
            </button>
            {remoteContent && (
              <button
                type="button"
                className="canvas-btn"
                onClick={() => navigator.clipboard.writeText(remoteContent)}
              >
                Скопировать
              </button>
            )}
          </>
        )}
      </div>

      <div className="canvas-body">
        {editing ? (
          <textarea
            ref={textareaRef}
            className="canvas-editor"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
          />
        ) : empty ? (
          <div className="canvas-empty">
            Холст пуст. Попросите агента написать сюда черновик, план,
            письмо или код — и он появится здесь.
          </div>
        ) : (
          <div className="canvas-content canvas-md">
            <Markdown remarkPlugins={MARKDOWN_PLUGINS}>{remoteContent}</Markdown>
          </div>
        )}
      </div>
    </div>
  );
}
